import React, { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router"
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from "react-redux"
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import DeleteModal from 'pages/parts/DeleteModal'
import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'
import useFilterSearchPagination from 'pages/parts/useFilterSearchPagination'
import DocumentStatusHistory from 'pages/parts/DocumentStatusHistory'

import { httpOk, GET, DELETE } from 'helpers/api'
import { formatDate, formatMoney, returnSign, findKeyFromArrayByValue } from "helpers/helpers"
import { SET_FILTER_DATA } from "store/filterData/actions"

function Index() {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const date = new Date()

	const reduxAccount = useSelector(state => state.account)

	const [data, setData] = useState({})
	const [statusHistoryId, setStatusHistoryId] = useState(null)
	const [documentStatuses, setDocumentStatuses] = useState([])
	const [poses, setPoses] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const {
		filterData,
		setFilterData,
		appliedFilterData,
		setAppliedFilterData,
		filterDataExist,
		setFilterDataExist,
		pagination,
		setPagination,
		patchFilterData,
		applySearch,
		applyFilters,
		paginate,
	} = useFilterSearchPagination({
		initialFilterData: {
			start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(new Date(), 'yyyy-MM-dd'),
			pos_id: reduxAccount.pos_id,
			document_status: null,
			search: '',
			page: null,
			size: 20,
		},
		initialPagination: {
			url: '/documents-breakdown',
			response: null,
		},
	})
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteModalParams, setDeleteModalParams] = useState({ id: 0, index: 0 })

	async function searchWithFilter(passedData = {}) {
		var filterUrl = "/documents-breakdown"
		var urlParams = ""
		var filterDataCopy = { ...filterData, ...(passedData.filterData || {}) }

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.document_status)
			urlParams += returnSign(urlParams) + 'document_status=' + filterDataCopy.document_status
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page
		if (filterDataCopy.size)
			urlParams += returnSign(urlParams) + 'size=' + filterDataCopy.size

		if (urlParams !== "")
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams
		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setAppliedFilterData(filterDataCopy)
			setPagination({
				...pagination,
				url: filterUrl,
				response: response.data,
				size: filterDataCopy.size || 20,
			})
			filterDataCopy.url = '/documents-breakdown'
			dispatch(SET_FILTER_DATA(filterDataCopy))
		}

		setDropdownIsOpen(false)
	}

	async function deleteItem() {
		const response = await DELETE(`/documents-breakdown/${deleteModalParams.id}`, {}, { loader: true })
		if (httpOk(response)) {
			searchWithFilter()
			setModalDelete(false)
		}
	}

	function resetFilter() {
		const nextFilterData = {
			start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(new Date(), 'yyyy-MM-dd'),
			pos_id: filterData.pos_id ?? reduxAccount.pos_id,
			status: null,
			page: null,
			size: 20,
		}
		setFilterData(nextFilterData)
		searchWithFilter({ filterData: nextFilterData })
		setFilterDataExist(false)
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	async function getPoses() {
		const response = await GET('/helper/poses', {}, { loader: true })
		if (httpOk(response)) setPoses(response.data)
	}

	function returnClassStatus(status) {
		if (status === 1) return "bg-success"
		if (status === -1) return "bg-danger"
		if (status === -2) return "bg-secondary"
		return ""
	}

	async function getDocumentStatuses() {
		const response = await GET('/helper/document-statuses?entity=document_breakdown')
		if (httpOk(response)) setDocumentStatuses(response.data)
	}

	useEffect(() => {
		getDocumentStatuses()
		getPoses()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('documents_breakdown')} [{data.total ?? 0}]
				</h4>
				<button type="button" className="btn btn-primary btn-rounded"
					onClick={() => navigate('/documents-breakdown/create')}>
					{t('create')}
				</button>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<FilterSearch
									value={filterData.search}
									onSearchChange={(search) => applySearch(search, (nextFilterData) => {
										searchWithFilter({ filterData: nextFilterData })
									})}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('pos')}</label>
												<Select
													options={poses}
													value={poses.find(option => option.id === filterData.pos_id) || ''}
													onChange={(option) => patchFilterData({ pos_id: option ? option.id : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.id}
												/>
											</div>
											<div className="form-group col-md-6">
												<label>{t('status')}</label>
												<Select
													isClearable
													options={documentStatuses}
													value={documentStatuses.find(option => option.value === filterData.document_status) || ''}
													onChange={(option) => patchFilterData({ 'document_status': option ? option.value : null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => t(option.value)}
													getOptionValue={(option) => option.value}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													withTime={false}
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
													onChange={(period) => patchFilterData(period)}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end">
													<button className="btn btn-secondary me-2" onClick={() => setDropdownIsOpen(false)}>{t('cancel')}</button>
													<button className="btn btn-primary" onClick={() => applyFilters((nextFilterData) => {
														searchWithFilter({ filterData: nextFilterData })
													})}>{t('apply')}</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
						</div>
						<div className="vertical-center">
							<h4><b>{t('amount')}:</b> {formatMoney(data?.total_amount)}</h4>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{(appliedFilterData.start_date || appliedFilterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{appliedFilterData.start_date &&
											<span>{formatDate(appliedFilterData.start_date)}</span>
										}
										{(appliedFilterData.start_date && appliedFilterData.end_date) &&
											<span>|</span>
										}
										{appliedFilterData.end_date &&
											<span>{formatDate(appliedFilterData.end_date)}</span>
										}
									</span>
								}
								{appliedFilterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', appliedFilterData.pos_id, 'name')}
									</span>
								}
								{appliedFilterData.document_status &&
									<span className="me-2">
										<b>{t('status')}: </b>
										{t(appliedFilterData.document_status)}
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ minWidth: '33px' }} className="text-center">#</th>
									<th style={{ width: '14%' }}>{t('pos')}</th>
									<th style={{ width: '14%' }}>{t('from_warehouse')}</th>
									<th style={{ width: '14%' }}>{t('to_warehouse')}</th>
									<th style={{ width: '12%' }}>{t('employee')}</th>
									<th style={{ width: '12%' }}>{t('note')}</th>
									<th style={{ width: '8%' }} className="text-center">{t('status')}</th>
									<th style={{ minWidth: '115px' }} className="text-nowrap text-center">{t('date')}</th>
									<th style={{ width: '10%' }} className="text-nowrap text-end">{t('amount')}</th>
									<th style={{ minWidth: '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={item.id ?? index}>
										<td className="text-center">{item.row_number ?? index + 1}</td>
										<td className="td-ellipsis">{item.pos_name}</td>
										<td className="td-ellipsis">{item.wms_from_name}</td>
										<td className="td-ellipsis">{item.wms_to_name}</td>
										<td className="td-ellipsis">{item.employee_name}</td>
										<td className="td-ellipsis">{item.note}</td>
										<td className={`text-center text-white ${returnClassStatus(item.status)}`}>
											{item.status === 1 && <span>{t('success')}</span>}
											{item.status === -1 && <span>{t('deleted')}</span>}
										</td>
										<td className="text-center">
											{formatDate(item.created_at, 'dd.MM.yy HH:mm')}
										</td>
										<td className="text-end text-nowrap">{formatMoney(item.total_source_cost_price)}</td>
										<td>
											<div className="table-dropdown">
												<Dropdown id={`dropdown-bd-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>
													<Dropdown.Menu>
														<Link to={`/documents-breakdown/preview/${item.id}`} className="table-dropdown-item">
															<i className="uil-eye"></i>
															{t('preview')}
														</Link>
														<div className="table-dropdown-item"
															onClick={() => setStatusHistoryId(item.id)}>
															<i className="uil-history"></i>
															{t('document_status_history')}
														</div>
														{/* Удаление — последним пунктом: промах мимо соседнего пункта дороже всего */}
														{item.status === 1 &&
															<div className="table-dropdown-item text-danger"
																onClick={() => {
																	setDeleteModalParams({ id: item.id, index })
																	setModalDelete(true)
																}}>
																<i className="uil uil-times"></i>
																{t('delete')}
															</div>
														}
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={(page) => {
								paginate(page)
								searchWithFilter({ filterData: { ...filterData, page } })
							}}>
						</FilterPagination>
					}
				</div>
			</div>

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>

			<DocumentStatusHistory
				entity="document_breakdown"
				documentId={statusHistoryId}
				setDocumentId={setStatusHistoryId}>
			</DocumentStatusHistory>
		</>
	)
}

export default Index
